import { createContext, useContext, useId, type ReactNode } from 'react';
import { INTAKE_HONEYPOT_FIELD } from '@steward-app/steward-types';
import { cls } from '../components/formStyles';
import type { SectionErrors } from './draft';

/**
 * The wizard's field wrappers (docs/25 S4).
 *
 * These add no styling of their own: every class comes from the shared `cls` object
 * the Portfolio forms already use, so the intake form and the app look like one
 * product. What they DO add is the label/description/error wiring — `htmlFor`,
 * `aria-describedby`, `aria-invalid` — which each section would otherwise
 * hand-roll, and which is exactly the part that quietly goes missing when hand-rolled.
 *
 * The same reasoning as `EntitySelect` in `pages/portfolio/forms.tsx`: small, local,
 * built from the house primitives, not a form library.
 */

/** Reads the message for a field path out of a section's error map. */
export function fieldError(errors: SectionErrors, path: string): string | undefined {
  return errors[path];
}

function describedBy(...parts: (string | undefined)[]): string | undefined {
  const ids = parts.filter(Boolean);
  return ids.length ? ids.join(' ') : undefined;
}

/**
 * The mobile keyboard `inputMode` hint for each `TextField` `type`. Cheap and
 * additive: it changes which keyboard a phone shows (a dialpad for a phone number,
 * an `@`-and-`.`-forward layout for email) and nothing else, so it is applied
 * wherever the type already implies it rather than field by field.
 */
const INPUT_MODE: Partial<Record<NonNullable<TextFieldProps['type']>, 'email' | 'tel'>> = {
  email: 'email',
  tel: 'tel',
};

/**
 * The native `<input>` `type` attribute for each `TextField` `type`. `money` is not
 * a real HTML input type — it exists only to select {@link formatMoneyOnBlur} above
 * — and rendering it as `text` is what keeps `inputMode` at its default (see
 * {@link INPUT_MODE}) so a numeric keypad never replaces the full keyboard a word
 * like "paid off" needs.
 */
const HTML_TYPE: Record<NonNullable<TextFieldProps['type']>, 'text' | 'email' | 'tel' | 'date'> = {
  text: 'text',
  email: 'email',
  tel: 'tel',
  date: 'date',
  money: 'text',
};

/**
 * Formats a US phone number to `(970) 000-0000`, but ONLY when the input reduces to
 * exactly 10 digits once everything else is stripped away. Anything else — a partial
 * number still being typed, an international number, a `+44…` country code, an
 * extension, letters — comes back unchanged. This is deliberately conservative: a
 * client is transcribing whatever number they use, not filling out a US-only phone
 * mask, and guessing wrong would silently corrupt a real answer. Callers apply this
 * on blur (see {@link TextField}) rather than on every keystroke, so formatting never
 * fights the caret while someone is still typing.
 */
export function formatPhoneOnBlur(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 10) return value;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Trims leading/trailing whitespace from an email address on blur. Deliberately does
 * NOT lowercase: the local part of an email address is technically case-sensitive,
 * and normalising it belongs where identity is actually resolved (appcore's own
 * `normaliseEmail`), not in a form that is just transcribing what someone told their
 * advisor.
 */
export function trimEmailOnBlur(value: string): string {
  return value.trim();
}

/**
 * Formats a money field to `$18,000` on blur, but ONLY when the value is a clean
 * number once punctuation is stripped: a leading `$`, thousands commas, and
 * surrounding spaces come off first, and what's left must match `^\d+(\.\d{1,2})?$`.
 * Everything else — `~$20k/mo`, `paid off`, `2M`, `1-2 million`, `n/a`, a `%` rate —
 * is deliberately conversational (`intakeSchema.ts` header, `MAX_AMOUNT` placeholders)
 * and comes back unchanged rather than guessed at.
 *
 * Decimals appear only when the person typed them, padded to exactly two places
 * (`18000.5` -> `$18,000.50`); a value with no decimal point never gets a forced
 * `.00` (founder decision, crossmarc/steward-app#1824). Idempotent: reformatting an
 * already-formatted `$18,000.50` strips back to `18000.50` and rebuilds the same
 * string, so re-blurring a field a person didn't touch is a no-op.
 *
 * This rewrites the STORED value, the same mechanism as {@link formatPhoneOnBlur}:
 * the draft, the firm's read view and the PDF (`fieldMoney`,
 * `src/advisor/intakeDocument.ts`) all see the rewritten string, not a second
 * display-only formatting layer.
 */
export function formatMoneyOnBlur(value: string): string {
  const stripped = value.trim().replace(/^\$/, '').replace(/,/g, '').trim();
  if (!/^\d+(\.\d{1,2})?$/.test(stripped)) return value;
  const [whole, decimal] = stripped.split('.');
  const grouped = Number(whole).toLocaleString('en-US');
  return decimal === undefined ? `$${grouped}` : `$${grouped}.${decimal.padEnd(2, '0')}`;
}

/**
 * How near the cap the text must be before the counter appears: 90 percent, so 180 of a 200
 * cap (founder decision D6, docs/43 slice 4b, 2026-11-01). A choice, not a derived figure. The
 * counter reads exactly "180 of 200 characters", with the cap the input enforces.
 */
export const COUNTER_FROM_FRACTION = 0.9;

/** The counter's text, or `null` while the value is still well short of the cap. */
export function counterText(length: number, max: number): string | null {
  return length >= Math.ceil(max * COUNTER_FROM_FRACTION) ? `${length} of ${max} characters` : null;
}

interface FieldShellProps {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  /** The value's length and the cap; the counter shows only near the cap ({@link counterText}). */
  counter?: { length: number; max: number };
  children: (wiring: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

function FieldShell({ id, label, hint, error, counter, children }: FieldShellProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const count = counter ? counterText(counter.length, counter.max) : null;
  // The counter is read as part of the field's description when it is focused, not announced on
  // every keystroke (a live region would speak once per character typed near the cap).
  const counterId = count ? `${id}-counter` : undefined;
  return (
    <div>
      <label className={cls.label} htmlFor={id}>{label}</label>
      {children({ id, describedBy: describedBy(hintId, errorId, counterId), invalid: !!error })}
      {count && <p id={counterId} className="mt-1 mb-0 text-right text-[12px] text-slate">{count}</p>}
      {hint && <p id={hintId} className="mt-1 mb-0 text-[12px] text-slate">{hint}</p>}
      {error && <p id={errorId} className={cls.error}>{error}</p>}
    </div>
  );
}

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** The question's unified cap (`cap('aboutYou.firstName')`, `caps.ts`). The counter shows near it. */
  maxLength: number;
  hint?: string;
  error?: string;
  /**
   * `date` is the one exception to this form's free-text-everywhere rule (docs/25,
   * intakeSchema.ts header): RIPL's own child "Date of birth" field is a native
   * `<input type="date">`, not a placeholder-driven guess like every rate/amount/
   * other date-ish field here, so the wizard matches that control type exactly.
   * The value stored is still a bounded STRING (the browser's own `YYYY-MM-DD`),
   * not a Date — the schema never parses it as one.
   *
   * `money` keeps `inputMode="text"` (see {@link INPUT_MODE}) rather than switching
   * to a numeric keypad: a client typing "paid off" or "n/a" into an amount field is
   * intentional (`intakeSchema.ts` header), and a numeric-only keyboard would make
   * those words harder to type on a phone.
   */
  type?: 'text' | 'email' | 'tel' | 'date' | 'money';
  autoComplete?: string;
  placeholder?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  /**
   * Read-only, with the same visual treatment the app uses elsewhere for a field the
   * person cannot change (crossmarc/steward-app#1484: About You's email once it is locked
   * to the verified address). Not a validation state — `error` still renders alongside it
   * if the caller has one.
   */
  disabled?: boolean;
}

export function TextField({
  label, value, onChange, maxLength, hint, error, type = 'text', autoComplete, placeholder,
  inputRef, disabled,
}: TextFieldProps) {
  const id = useId();
  // Formatting happens on blur, and only for the types that have a safe,
  // conservative rule (phone: exactly 10 digits; email: trim only; money: a clean
  // number only). Every other type gets no rewriting at all, and money's own rule
  // still leaves anything non-numeric — "unknown" / "paid off" / "n/a" — untouched.
  const handleBlur = () => {
    if (type === 'tel') {
      const formatted = formatPhoneOnBlur(value);
      if (formatted !== value) onChange(formatted);
    } else if (type === 'email') {
      const trimmed = trimEmailOnBlur(value);
      if (trimmed !== value) onChange(trimmed);
    } else if (type === 'money') {
      const formatted = formatMoneyOnBlur(value);
      if (formatted !== value) onChange(formatted);
    }
  };
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} counter={{ length: value.length, max: maxLength }}>
      {(w) => (
        <input
          ref={inputRef}
          id={w.id}
          type={HTML_TYPE[type]}
          inputMode={INPUT_MODE[type]}
          className={`${cls.input} ${disabled ? 'disabled:cursor-default disabled:opacity-70' : ''}`}
          value={value}
          maxLength={maxLength}
          autoComplete={autoComplete}
          placeholder={placeholder}
          aria-describedby={w.describedBy}
          aria-invalid={w.invalid || undefined}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={handleBlur}
        />
      )}
    </FieldShell>
  );
}

interface TextAreaFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  hint?: string;
  error?: string;
  rows?: number;
  placeholder?: string;
}

export function TextAreaField({
  label, value, onChange, maxLength, hint, error, rows = 4, placeholder,
}: TextAreaFieldProps) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} counter={{ length: value.length, max: maxLength }}>
      {(w) => (
        <textarea
          id={w.id}
          className={cls.textarea}
          rows={rows}
          value={value}
          maxLength={maxLength}
          placeholder={placeholder}
          aria-describedby={w.describedBy}
          aria-invalid={w.invalid || undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </FieldShell>
  );
}

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** The schema's own constant. Never a list restated in the UI. */
  options: readonly string[];
  labels: Record<string, string>;
  /**
   * Default "Select one" matches RIPL's own neutral placeholder (docs/evidence/
   * ripl-intake-form) — never a real option defaulted. A "kind of ___" select
   * overrides this to "Select type", RIPL's wording for that specific case.
   */
  placeholder?: string;
  hint?: string;
  error?: string;
}

export function SelectField({
  label, value, onChange, options, labels, placeholder = 'Select one', hint, error,
}: SelectFieldProps) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} hint={hint} error={error}>
      {(w) => (
        <select
          id={w.id}
          className={cls.select}
          value={value}
          aria-describedby={w.describedBy}
          aria-invalid={w.invalid || undefined}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option} value={option}>{labels[option] ?? option}</option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

interface CheckboxGroupProps {
  legend: string;
  /** The schema's own constant. Never a list restated in the UI. */
  options: readonly string[];
  labels: Record<string, string>;
  value: string[];
  onChange: (value: string[]) => void;
  hint?: string;
  error?: string;
}

/**
 * Multi-select as real checkboxes in a fieldset, so the group has one name a screen
 * reader announces and each box is reachable by keyboard on its own. Order follows the
 * schema constant, and a selection is never duplicated (the schema rejects duplicates).
 */
export function CheckboxGroup({ legend, options, labels, value, onChange, hint, error }: CheckboxGroupProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const toggle = (option: string) => {
    onChange(value.includes(option) ? value.filter((v) => v !== option) : [...value, option]);
  };
  return (
    <fieldset className="m-0 border-0 p-0" aria-describedby={describedBy(hintId, errorId)}>
      <legend className={`${cls.label} p-0`}>{legend}</legend>
      {hint && <p id={hintId} className="mt-0 mb-2 text-[12px] text-slate">{hint}</p>}
      <div className="flex flex-col gap-2">
        {options.map((option) => (
          <label key={option} className="flex items-start gap-2.5 text-[13px] text-navy cursor-pointer max-sm:min-h-11 max-sm:items-center">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 max-sm:h-5 max-sm:w-5 shrink-0 accent-bronze cursor-pointer"
              checked={value.includes(option)}
              onChange={() => toggle(option)}
            />
            <span>{labels[option] ?? option}</span>
          </label>
        ))}
      </div>
      {error && <p id={errorId} className={cls.error}>{error}</p>}
    </fieldset>
  );
}

/** A single yes/no answer, rendered as one checkbox with its statement as the label. */
export function CheckboxField({ label, checked, onChange, hint }: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div>
      <label className="flex items-start gap-2.5 text-[13px] text-navy cursor-pointer max-sm:min-h-11 max-sm:items-center">
        <input
          id={id}
          type="checkbox"
          className="mt-0.5 h-4 w-4 max-sm:h-5 max-sm:w-5 shrink-0 accent-bronze cursor-pointer"
          checked={checked}
          aria-describedby={hintId}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{label}</span>
      </label>
      {hint && <p id={hintId} className="mt-1 mb-0 text-[12px] text-slate">{hint}</p>}
    </div>
  );
}

/**
 * True inside the firm's staff entry form (docs/43 slice 8, steward-app#2770). The sections are the
 * client's own, and their notes speak to the client ("Let us start with you..."). In staff mode a
 * note shows its `staff` wording, or nothing when it only addresses the client.
 */
export const StaffModeContext = createContext(false);

/**
 * An explanatory paragraph above a group of fields. `staff` is what staff entry shows instead:
 * short wording that instructs the person entering, `true` when the note already reads the same
 * to staff (a plain list), and left out when the note only addresses the client (then staff see
 * nothing). The client's own form never reads it.
 */
export function SectionNote({ children, staff }: { children: ReactNode; staff?: ReactNode | true }) {
  const staffMode = useContext(StaffModeContext);
  if (staffMode) {
    if (staff === undefined) return null;
    return <p className="mt-0 mb-5 text-[13px] leading-relaxed text-slate">{staff === true ? children : staff}</p>;
  }
  return <p className="mt-0 mb-5 text-[13px] leading-relaxed text-slate">{children}</p>;
}

/**
 * The honeypot the façade checks (`INTAKE_HONEYPOT_FIELD`, docs/25 security checklist
 * §1). Named like a field a real form would have, and sent with the basics
 * (`IntakeBasics.tsx`, the form's one unauthenticated write) whether or not anything filled
 * it in. `IntakeWizard.tsx` still renders one and strips it from what it saves.
 *
 * Hidden three ways, because one is not enough: visually (the app's own `sr-only` clip,
 * which a bot reading the DOM still sees, unlike `display: none`), from the keyboard
 * (`tabIndex={-1}`, so Tab never lands on it), and from assistive technology (`aria-hidden`
 * on the wrapper, so no screen reader ever offers it as something to fill in).
 * `autoComplete="off"` keeps a browser's own autofill away from it too.
 */
export function HoneypotField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div aria-hidden="true" className="sr-only">
      <label htmlFor="intake-website">Website</label>
      <input
        id="intake-website"
        name={INTAKE_HONEYPOT_FIELD}
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
